import { createApi } from "@reduxjs/toolkit/query/react";
import {
  listDevicesResponse,
  Device,
  scheduleDeviceUpdateResponse,
  displayNameUpdateResponse,
} from "../../types";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import { withErrorNotification } from "../notifications";

export const deviceApi = createApi({
  reducerPath: "deviceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Device"],
  endpoints: (builder) => ({
    // `org` is optional and only the superuser panel passes it; it is part of
    // the query arg so the cache key differs per organization.
    listDevices: builder.query<listDevicesResponse, { org?: string } | void>({
      query: (args) => ({ url: "/devices", org: args?.org }),
      providesTags: ["Device"],
      onQueryStarted: withErrorNotification("Failed to list devices"),
    }),
    getDeviceById: builder.query<Device, string>({
      query: (deviceUuid) => ({ url: `/device/${deviceUuid}` }),
      providesTags: (result, error, id) => [{ type: "Device", id }],
      onQueryStarted: withErrorNotification("Failed to fetch device details"),
    }),
    scheduleDeviceUpdate: builder.mutation<
      scheduleDeviceUpdateResponse,
      { deviceUuid: string; will_update: boolean }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}/firmware`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to schedule device update"),
    }),
    displayNameUpdate: builder.mutation<
      displayNameUpdateResponse,
      { deviceUuid: string; display_name: string }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to update display name"),
    }),
    deviceWifiUpdate: builder.mutation<
      displayNameUpdateResponse,
      { deviceUuid: string; wifi: string }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}/wifi`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to update wifi"),
    }),
  }),
});

export const {
  useListDevicesQuery,
  useGetDeviceByIdQuery,
  useLazyGetDeviceByIdQuery,
  useScheduleDeviceUpdateMutation,
  useDisplayNameUpdateMutation,
  useDeviceWifiUpdateMutation,
} = deviceApi;
